import { Menu } from 'lucide-react'

import { cn } from '@/custolink-newdesign/lib/utils'
import ShadcnScope from '@/custolink-newdesign/components/ShadcnScope'
import { Button } from '@/custolink-newdesign/ui/button'
import { Separator } from '@/custolink-newdesign/ui/separator'
import { TooltipProvider } from '@/custolink-newdesign/ui/tooltip'
import useUiContext from '@/custolink-hooks/useUiContext'
import NotificationsMenu from '@/custolink-newdesign/shell/NotificationsMenu'
import IncomingCallIndicator from '@/custolink-newdesign/shell/IncomingCallIndicator'

/**
 * The topbar. Skeleton and classes are flow's Toolbar.tsx:126-186.
 *
 * flow's toolbar carries a ProjectSwitcher, a global ItemSearch pill and a
 * "New item" button. This app has no global search and no global create, so
 * rather than invent them the tracks are filled with what actually exists:
 *
 *   flow                        -> here
 *   ProjectSwitcher             -> OrgMark (organisation slug)
 *   ItemSearch (centre track)   -> empty; the centre track still flexes so the
 *                                  right-hand group stays pinned right
 *   NewItemButton + utilities   -> IncomingCallIndicator + NotificationsMenu
 *
 * The user menu is NOT here: flow puts identity at the foot of the rail, so it
 * lives in NavbarContent's UserFooter.
 */

/** Stands in for flow's ProjectSwitcher - read-only, this app is single-tenant per host. */
function OrgMark() {
  const uic = useUiContext()
  const slug = uic.organisationContext?.organisationSlug
  const label = uic.controlpanelContext ? 'Control Panel' : 'Administration'

  return (
    <div className="cl:flex cl:max-w-56 cl:items-center cl:gap-2 cl:px-2">
      <span className="cl:truncate cl:text-sm cl:font-semibold">{label}</span>
      {slug ? (
        <span className="cl:bg-muted cl:text-muted-foreground cl:rounded cl:px-1.5 cl:py-0.5 cl:text-[11px] cl:font-semibold cl:tracking-wide">
          {slug}
        </span>
      ) : null}
    </div>
  )
}

export default function Toolbar({
  className,
  onToggleNavbar,
}: {
  className?: string
  onToggleNavbar: () => void
}) {
  return (
    <TooltipProvider delayDuration={400}>
      <ShadcnScope
        id="app-toolbar"
        className={cn(
          'cl:bg-background cl:text-foreground cl:relative cl:z-20 cl:flex cl:w-full cl:shrink-0',
          className
        )}
      >
        <div className="cl:flex cl:min-h-12 cl:w-full cl:items-center cl:gap-2 cl:px-2 cl:md:min-h-16 cl:md:gap-3 cl:md:px-4">
          <div className="cl:flex cl:shrink-0 cl:items-center cl:gap-2 cl:md:gap-3">
            {/* 36px on touch, 32px on desktop - the repeated icon-button size. */}
            <Button
              variant="outline"
              size="icon-sm"
              aria-label="Toggle navigation"
              className="cl:text-muted-foreground cl:hover:text-foreground cl:bg-card cl:size-9 cl:md:size-8"
              onClick={onToggleNavbar}
            >
              <Menu />
            </Button>
            <Separator
              orientation="vertical"
              className="cl:data-[orientation=vertical]:h-5"
            />
            <OrgMark />
          </div>

          {/* Centre: the only flexing track. Empty until there is a global search. */}
          <div className="cl:flex cl:min-w-0 cl:flex-1 cl:justify-center" />

          <div className="cl:flex cl:shrink-0 cl:items-center cl:gap-1 cl:md:gap-2">
            {/* Re-skinned visuals only - both reuse the exact hooks, atoms and
                mutations of the Ecme components they replace
                (custolink-components/navigation/IncomingVideoSupport.tsx and
                .../Notification/Notification.tsx). */}
            <IncomingCallIndicator />
            <NotificationsMenu />
          </div>
        </div>
      </ShadcnScope>
    </TooltipProvider>
  )
}
